import type { Rng } from '../../math/rng.ts';
import type { ProblemTemplate } from '../../types.ts';
import {
  coefficient,
  continuation,
  exactDiv,
  integerProblem,
  LETTER_PAIRS,
  linear,
  signedBlank,
  signedInt,
  span,
  type Term,
  UNKNOWNS,
} from './helpers-g05-08.ts';

/**
 * Grade 8 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 8: linear equations with integers (unknown on both sides,
 * brackets), the exponent laws for multiplication, division and powers of
 * powers, adding and subtracting like terms, and multiplying out /
 * simplifying monomial × binomial expressions.
 */

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

/** `x^{e}`, or the bare letter when e = 1 (never `x^1`). */
function power(v: string, e: number): string {
  return e === 1 ? v : `${v}^{${e}}`;
}

const bothSides: ProblemTemplate = {
  id: 'g8.both-sides',
  grade: 8,
  title: 'Solve ax + b = cx + d (integers)',
  parTimeMs: 17000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    // Every constant stays within ±limit: 12 at difficulty 0, 25 at the top.
    const limit = span(difficulty, 12, 25);
    // a − c (the collected coefficient) and x are chosen so that k = (a − c)·x = e − b fits in ±limit.
    const diff = rng.int(1, span(difficulty, 4, 7));
    const xMagnitude = rng.int(1, Math.max(1, Math.min(span(difficulty, 6, 12), Math.floor(limit / diff))));
    // Positive answers at difficulty 0; negative ones from the lower-middle band up.
    const x = (difficulty >= 0.3 && rng.chance(0.5) ? -1 : 1) * xMagnitude;
    // A negative x-coefficient on the other side only in the upper band (a = c + diff must not be 0).
    const c =
      difficulty >= 0.6 && rng.chance(0.3)
        ? -rng.intExcept(1, span(difficulty, 3, 5), [diff])
        : rng.int(1, span(difficulty, 6, 10));
    const a = c + diff;
    const k = diff * x; // e − b
    // b and e = k + b both in ±limit, and neither 0 (no "x + 0" on either side).
    const b = rng.intExcept(Math.max(-limit, -limit - k), Math.min(limit, limit - k), [0, -k]);
    const e = k + b;
    const left = linear([{ coef: a, v }, { coef: b }]);
    const right = linear([{ coef: c, v }, { coef: e }]);
    const swap = difficulty >= 0.5 && rng.chance(0.5);
    return integerProblem({
      prompt: swap ? `${right} = ${left}` : `${left} = ${right}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors: [
        -x, // moved the constants across without changing their signs
        exactDiv(e + b, a - c), // sign slip on one constant
        exactDiv(e - b, a + c), // sign slip on one x-term (added instead of subtracting)
        e - b, // forgot to divide by the collected coefficient
      ],
    });
  },
};

const brackets: ProblemTemplate = {
  id: 'g8.brackets',
  grade: 8,
  title: 'Solve a(x ± b) = c',
  parTimeMs: 15000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = (difficulty >= 0.8 && rng.chance(0.4) ? -1 : 1) * rng.int(2, span(difficulty, 5, 8));
    const pMax = span(difficulty, 6, 12);
    // Pick the bracket's value first so c = a·(x + p) stays a friendly size.
    const valueMax = span(difficulty, 8, 12);
    let p: number;
    let bracketValue: number;
    if (difficulty < 0.35) {
      // Low band: a positive bracket (so c > 0) and x = value − p ≥ 1.
      bracketValue = rng.int(2, valueMax);
      p = rng.chance(0.5) ? rng.int(1, bracketValue - 1) : -rng.int(1, pMax);
    } else {
      // Any signs; p ≠ value keeps x ≠ 0 (and value ≠ 0 keeps c ≠ 0).
      bracketValue = signedInt(rng, 1, valueMax);
      const pSign = rng.sign();
      p = pSign * rng.intExcept(1, pMax, pSign === Math.sign(bracketValue) ? [Math.abs(bracketValue)] : []);
    }
    const x = bracketValue - p;
    const c = a * (x + p);
    return integerProblem({
      prompt: `${coefficient(a)}(${linear([{ coef: 1, v }, { coef: p }])}) = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors: [
        c / a + p, // undid the bracket's ± p the wrong way round
        exactDiv(c - p, a), // multiplied only the x by a when expanding
        c - p, // forgot to divide by a
        -x, // sign slip on the answer
      ],
    });
  },
};

const expLawMult: ProblemTemplate = {
  id: 'g8.exp-law-mult',
  grade: 8,
  title: 'Exponent law: x^a · x^b = x^□',
  parTimeMs: 8000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const max = span(difficulty, 6, 12);
    const a = rng.int(2, max);
    const b = a === 2 ? rng.int(3, max) : rng.int(2, max); // (2, 2) would make a + b = a·b
    const forms = ['two', ...(difficulty >= 0.4 ? ['bare'] : []), ...(difficulty >= 0.7 ? ['coefficients'] : [])];
    const form = rng.pick(forms);
    let prompt: string;
    let answer: number;
    let distractors: (number | null)[];
    if (form === 'bare') {
      // x^a · x · x^b: the middle x is x¹.
      prompt = `${power(v, a)} \\cdot ${v} \\cdot ${power(v, b)} = ${v}^{\\box}`;
      answer = a + b + 1;
      distractors = [
        a + b, // counted the bare x as x⁰
        a * b, // multiplied the exponents
      ];
    } else if (form === 'coefficients') {
      answer = a + b;
      // No coefficient (p, q or pq) may equal the answer, or it could be copied from the prompt.
      const p = rng.intExcept(2, 5, [answer]);
      const q = rng.intExcept(2, 5, [answer, answer / p]);
      prompt = `${p}${power(v, a)} \\cdot ${q}${power(v, b)} = ${p * q}${v}^{\\box}`;
      distractors = [
        a * b, // multiplied the exponents (as with the coefficients)
        a === b ? null : Math.abs(a - b), // subtracted the exponents (the division law)
      ];
    } else {
      prompt = `${power(v, a)} \\cdot ${power(v, b)} = ${v}^{\\box}`;
      answer = a + b;
      distractors = [
        a * b, // multiplied the exponents
        a === b ? null : Math.abs(a - b), // subtracted the exponents (the division law)
      ];
    }
    return integerProblem({ prompt, answer, allowNegative: true, distractors });
  },
};

const expLawPower: ProblemTemplate = {
  id: 'g8.exp-law-power',
  grade: 8,
  title: 'Exponent law: (x^a)^b = x^□',
  parTimeMs: 8000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 5, 9));
    const withCoefficient = difficulty >= 0.6 && rng.chance(0.4);
    // (px^a)^b shows p^b on the right, so keep b small there (2^b ≤ 16, 3^b ≤ 81).
    const bMax = withCoefficient ? 4 : span(difficulty, 4, 6);
    const b = a === 2 ? rng.int(3, bMax) : rng.int(2, bMax); // (2, 2) would make a + b = a·b
    const inner = power(v, a);
    let prompt: string;
    if (withCoefficient) {
      // Skip p where p^b equals the answer ("(2x⁴)⁴ = 16x^□"): the answer would be copyable.
      const p = rng.pick([2, 3].filter((base) => base ** b !== a * b));
      prompt = `(${p}${inner})^{${b}} = ${p ** b}${v}^{\\box}`;
    } else {
      prompt = `(${inner})^{${b}} = ${v}^{\\box}`;
    }
    return integerProblem({
      prompt,
      answer: a * b,
      allowNegative: true,
      distractors: [
        a + b, // added the exponents (the multiplication law)
        a ** b <= 200 ? a ** b : null, // raised the exponent to the power
        a * (b + 1), // miscounted the repeated factors when writing (x^a)^b out in full
      ],
    });
  },
};

const expLawDiv: ProblemTemplate = {
  id: 'g8.exp-law-div',
  grade: 8,
  title: 'Exponent law: x^a ÷ x^b = x^□',
  parTimeMs: 8000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(5, span(difficulty, 9, 20));
    // A bare x (x¹) as the divisor only from the middle band up. b = a/2 is skipped: the
    // answer would equal the divisor's exponent ("x⁶ ÷ x³ = x^□") and could be copied.
    const b = difficulty >= 0.5 && rng.chance(0.25) ? 1 : rng.intExcept(2, a - 2, [a / 2]);
    const top = power(v, a);
    const bottom = power(v, b);
    const asFraction = difficulty >= 0.5 && rng.chance(0.5);
    return integerProblem({
      prompt: asFraction ? `\\frac{${top}}{${bottom}} = ${v}^{\\box}` : `${top} \\div ${bottom} = ${v}^{\\box}`,
      answer: a - b,
      allowNegative: true,
      distractors: [
        b === 1 ? null : exactDiv(a, b), // divided the exponents
        a + b, // added the exponents (the multiplication law)
        b - a, // subtracted the wrong way round
        b === 1 ? a : null, // counted the bare x as x⁰
      ],
    });
  },
};

const expandCoef: ProblemTemplate = {
  id: 'g8.expand-coef',
  grade: 8,
  title: 'Expand a(bx + c): fill a term',
  parTimeMs: 10000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = (difficulty >= 0.5 && rng.chance(0.4) ? -1 : 1) * rng.int(2, span(difficulty, 5, 9));
    const b = rng.int(1, span(difficulty, 3, 6));
    // Positive constants in the gentle band. |c| ≠ b, so the constant term a·c never equals the
    // x-coefficient a·b: whichever one is blank, the other (shown on the right) can't be copied.
    const gentle = difficulty < 0.3;
    const cSign = gentle ? 1 : rng.sign();
    const c = cSign * rng.intExcept(1, gentle ? 6 : span(difficulty, 6, 12), [b]);
    const lhs = `${coefficient(a)}(${linear([{ coef: b, v }, { coef: c }])})`;
    // Mostly the constant is blank; from the middle band the x-coefficient sometimes is.
    if (difficulty >= 0.5 && rng.chance(0.35)) {
      return integerProblem({
        prompt: `${lhs} = \\box ${v}${continuation({ coef: a * c })}`,
        answer: a * b,
        allowNegative: true,
        distractors: [
          b, // multiplied only the constant term by a
          a + b, // added instead of multiplying
          -a * b, // sign slip
        ],
      });
    }
    // 5(2a - 4) = 10a - □ → 20: a negative constant keeps its sign outside the box.
    const blank = signedBlank(a * c);
    return integerProblem({
      prompt: `${lhs} = ${linear([{ coef: a * b, v }])}${blank.sign}\\box`,
      answer: blank.answer,
      allowNegative: true,
      distractors: [
        blank.fill(c), // multiplied only the first term by a
        blank.fill(a + c), // added instead of multiplying
        blank.fill(-a * c), // sign slip (negative × negative)
      ],
    });
  },
};

const bracketsBoth: ProblemTemplate = {
  id: 'g8.brackets-both',
  grade: 8,
  title: 'Solve a(x + p) = c(x + q)',
  parTimeMs: 20000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const coefMax = span(difficulty, 4, 6);
    const a = rng.int(2, coefMax);
    const c = rng.intExcept(2, coefMax, [a]);
    // Both brackets equal a common multiple at the solution: a·u = c·w with u = m·c/g, w = m·a/g.
    // Building it this way guarantees whole-number constants without retries.
    const g = gcd(a, c);
    const m = (difficulty >= 0.5 && rng.chance(0.4) ? -1 : 1) * rng.int(1, span(difficulty, 1, 3));
    const u = (m * c) / g;
    const w = (m * a) / g;
    const xMax = span(difficulty, 6, 12);
    // x ≠ u and x ≠ w keep both bracket constants non-zero (no "x + 0").
    const x =
      difficulty < 0.5
        ? rng.intExcept(1, xMax, [u, w])
        : rng.sign() * rng.intExcept(1, xMax, [Math.abs(u), Math.abs(w)]);
    const p = u - x;
    const q = w - x;
    const bracket = (k: number, constant: number) => `${k}(${linear([{ coef: 1, v }, { coef: constant }])})`;
    // Moving a constant across without changing its sign can land far outside the answer range
    // (e.g. −290 for x = −10); a choice that implausible is no trap, so keep it only when it's believable.
    const signSlip = exactDiv(c * q + a * p, a - c);
    return integerProblem({
      prompt: `${bracket(a, p)} = ${bracket(c, q)}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors: [
        exactDiv(q - p, a - c), // multiplied only the x-terms when expanding
        signSlip !== null && Math.abs(signSlip) <= 3 * xMax ? signSlip : null, // sign slip moving a constant across
        c * q - a * p, // forgot to divide by the collected coefficient
        -x, // sign slip on the answer
      ],
    });
  },
};

const simplifyCoef: ProblemTemplate = {
  id: 'g8.simplify-coef',
  grade: 8,
  title: 'Expand and simplify a(bx + c) + dx = □x + e',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = (difficulty >= 0.6 && rng.chance(0.4) ? -1 : 1) * rng.int(2, span(difficulty, 3, 5));
    const b = rng.int(1, span(difficulty, 3, 5));
    const gentle = difficulty < 0.3;
    const c = gentle ? rng.int(1, 5) : signedInt(rng, 1, span(difficulty, 5, 9));
    const dMax = span(difficulty, 5, 9);
    const ab = a * b;
    const ac = a * c;
    // d ≠ −ab keeps the answer ≠ 0 (no "0x"); ab + d ≠ ±ac keeps the answer from repeating the
    // constant already written on the right-hand side.
    const sign = gentle ? 1 : rng.sign();
    const forbidden = [-ab, ac - ab, -ac - ab].filter((x) => Math.sign(x) === sign).map(Math.abs);
    const d = sign * rng.intExcept(1, dMax, forbidden);
    return integerProblem({
      prompt: `${coefficient(a)}(${linear([{ coef: b, v }, { coef: c }])})${continuation({ coef: d, v })} = \\box ${v}${continuation({ coef: ac })}`,
      answer: ab + d,
      allowNegative: true,
      distractors: [
        ab, // left out the dx term
        a + b + d, // added a and b instead of multiplying
        ab - d, // sign slip on the dx term
        ab + d + ac, // combined the constant with the x-terms
      ],
    });
  },
};

/**
 * Id keeps its original `g6.` prefix (ids are stored in run logs and never
 * renamed); CAPS places adding/subtracting like terms in Grade 8, so it lives here.
 */
const likeTermsCoef: ProblemTemplate = {
  id: 'g6.like-terms-coef',
  grade: 8,
  title: 'Add/subtract like terms: ax + bx = □x',
  parTimeMs: 9000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const max = span(difficulty, 6, 12);
    const forms = [
      'sum',
      ...(difficulty >= 0.3 ? ['difference', 'bare'] : []),
      ...(difficulty >= 0.6 ? ['three'] : []),
    ];
    const form = rng.pick(forms);
    let coefs: number[];
    if (form === 'difference') {
      // a − b ≥ 2 so the answer is never 1 or 0.
      const a = rng.int(4, Math.max(4, span(difficulty, 9, 15)));
      coefs = [a, -rng.int(2, a - 2)];
    } else if (form === 'bare') {
      // One term is a bare letter (coefficient 1) — the implicit-1 trap.
      const other = rng.int(2, max);
      coefs = rng.chance(0.5) ? [1, other] : [other, 1];
    } else if (form === 'three') {
      const a = rng.int(2, max);
      const b = rng.int(2, max);
      const c = rng.chance(0.5) ? rng.int(2, max) : -rng.int(2, Math.min(max, a + b - 2));
      coefs = [a, b, c];
    } else {
      coefs = [rng.int(2, max), rng.int(2, max)];
    }
    const answer = coefs.reduce((s, c) => s + c, 0);
    const bareTotal = coefs.filter((c) => Math.abs(c) === 1).reduce((s, c) => s + c, 0);
    const terms: Term[] = coefs.map((coef) => ({ coef, v }));
    const [first, second] = [coefs[0]!, coefs[1]!];
    return integerProblem({
      prompt: `${linear(terms)} = \\box ${v}`,
      answer,
      allowNegative: true,
      distractors: [
        coefs.reduce((s, c) => s + Math.abs(c), 0), // ignored the minus sign and added every coefficient
        bareTotal === 0 ? null : answer - bareTotal, // counted a bare letter as 0 instead of 1
        first * second, // multiplied the coefficients (mixing up 3x + 5x with 3x × 5x)
        second > 0 && first !== second ? Math.abs(first - second) : null, // used the wrong operation: subtracted
      ],
    });
  },
};

/** A left-hand-side term of g7.like-terms-mixed (a Grade 8 template): a coefficient on the first (0) or second (1) letter. */
interface LetterTerm {
  coef: number;
  letter: 0 | 1;
}

/**
 * Left-hand side for g7.like-terms-mixed, scaled by difficulty. The first term is always positive.
 * Every coefficient and both collected totals stay single-digit (and non-zero, so no "0a"), which
 * keeps the whole prompt within 28 characters: "9a − 8b − 2a + 9b = □ a + b".
 * The two collected totals never share a magnitude, so the coefficient already written on the
 * right-hand side is never also the answer.
 */
function likeTermsLhs(rng: Rng, difficulty: number): LetterTerm[] {
  if (difficulty < 0.3) {
    // Gentle: three positive terms, only one letter needs collecting (5a + 2b + 3a).
    const a1 = rng.int(1, 6);
    const a3 = rng.int(1, 6);
    return [
      { coef: a1, letter: 0 },
      { coef: rng.intExcept(2, 6, [a1 + a3]), letter: 1 },
      { coef: a3, letter: 0 },
    ];
  }
  const max = span(difficulty, 6, 9);
  let a1: number;
  let aTotal: number;
  let b1: number;
  let bTotal: number;
  if (difficulty < 0.65) {
    // Exactly one subtraction; both totals positive. The subtracted letter starts above its total.
    aTotal = rng.int(2, max - 1);
    bTotal = rng.intExcept(2, max - 1, [aTotal]);
    const subtractA = rng.chance(0.5);
    a1 = subtractA ? rng.int(aTotal + 1, max) : rng.int(1, aTotal - 1);
    b1 = subtractA ? rng.int(1, bTotal - 1) : rng.int(bTotal + 1, max);
  } else {
    // Any signs after the first term; totals may be negative. a1 ≤ total + max keeps |a2| ≤ max.
    aTotal = rng.intExcept(-(max - 2), max, [0]);
    a1 = rng.intExcept(2, Math.min(max, aTotal + max), [aTotal]);
    bTotal = rng.sign() * rng.intExcept(1, max, [Math.abs(aTotal)]);
    b1 = rng.intExcept(Math.max(-max, bTotal - max), Math.min(max, bTotal + max), [0, bTotal]);
  }
  const second: LetterTerm = { coef: aTotal - a1, letter: 0 };
  const fourth: LetterTerm = { coef: bTotal - b1, letter: 1 };
  return [{ coef: a1, letter: 0 }, { coef: b1, letter: 1 }, ...(rng.chance(0.5) ? [second, fourth] : [fourth, second])];
}

/**
 * Id keeps its original `g7.` prefix (ids are stored in run logs and never
 * renamed); CAPS places collecting like terms in Grade 8, so it lives here.
 */
const likeTermsMixed: ProblemTemplate = {
  id: 'g7.like-terms-mixed',
  grade: 8,
  title: 'Collect like terms in two letters, fill one coefficient',
  parTimeMs: 12000,
  allowNegative: true,
  generate(rng, difficulty) {
    const letters = rng.pick(LETTER_PAIRS);
    const lhsTerms = likeTermsLhs(rng, difficulty);
    const termsOf = (letter: 0 | 1) => lhsTerms.filter((t) => t.letter === letter).map((t) => t.coef);
    const sum = (xs: readonly number[]) => xs.reduce((s, x) => s + x, 0);
    const totals = [sum(termsOf(0)), sum(termsOf(1))] as const;
    // Gentle problems always blank the letter that actually needs collecting. A negative first total is
    // always the blank too: "-7a + □ b" would push the prompt past 28 characters.
    const blank: 0 | 1 = difficulty < 0.3 || totals[0] < 0 || rng.chance(0.5) ? 0 : 1;
    const lhs = linear(lhsTerms.map<Term>((t) => ({ coef: t.coef, v: letters[t.letter] })));
    // A blank second coefficient keeps a negative total's sign outside the box: "p - □q", never "p + □q" → −3.
    const tail = signedBlank(totals[1]);
    const fill = blank === 1 ? tail.fill : (signed: number | null) => signed;
    const rhs =
      blank === 0
        ? `\\box ${letters[0]}${continuation({ coef: totals[1], v: letters[1] })}`
        : `${coefficient(totals[0])}${letters[0]}${tail.sign}\\box ${letters[1]}`;
    const total = totals[blank];
    const blankCoefs = termsOf(blank);
    const bare = sum(blankCoefs.filter((c) => Math.abs(c) === 1));
    const anyNegative = lhsTerms.some((t) => t.coef < 0);
    return integerProblem({
      prompt: `${lhs} = ${rhs}`,
      answer: blank === 1 ? tail.answer : total,
      allowNegative: true,
      distractors: [
        fill(blankCoefs[0] ?? null), // didn't collect: kept only the first term of that letter
        fill(sum(blankCoefs.map(Math.abs))), // ignored the minus signs
        bare === 0 ? null : fill(total - bare), // counted a bare letter as 0 instead of 1
        fill(totals[0] + totals[1]), // combined unlike terms: added every coefficient
        anyNegative ? fill(-total) : null, // sign slip on the collected total
      ],
    });
  },
};

export const GRADE_08: ProblemTemplate[] = [
  bothSides,
  brackets,
  expLawMult,
  expLawPower,
  expLawDiv,
  expandCoef,
  likeTermsCoef,
  bracketsBoth,
  simplifyCoef,
  likeTermsMixed,
];
